import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { SupabaseService } from './supabase.service';
import { I18nService } from '../i18n/i18n.service';

/**
 * Browser companion for active timer awareness while LifeOps is running.
 * Real push while the browser is closed requires a server-side Web Push sender.
 */
@Injectable({ providedIn: 'root' })
export class ActiveTimerNotificationService {
  private readonly sb = inject(SupabaseService);
  private readonly i18n = inject(I18nService);
  private timerId: ReturnType<typeof setInterval> | null = null;
  private notifiedTimerId: number | null = null;
  private activeTag = 'lifeops-active-timer';
  private get client(): SupabaseClient { return this.sb.client as SupabaseClient; }

  start(): void {
    if (typeof window === 'undefined' || this.timerId !== null) return;
    this.timerId = window.setInterval(() => { void this.poll(); }, 60_000);
    void this.poll();
  }

  stop(): void {
    if (this.timerId !== null) clearInterval(this.timerId);
    this.timerId = null;
    this.notifiedTimerId = null;
  }

  async unregisterDevice(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        const owner = this.sb.userId;
        if (owner) await this.client.from('notification_push_subscriptions')
          .delete().eq('owner_id', owner).eq('endpoint', subscription.endpoint);
        await subscription.unsubscribe();
      }
      await this.dismiss();
      this.notifiedTimerId = null;
    } catch { /* Signing out must remain available even without PushManager. */ }
  }

  private async dismiss(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const notifications = await registration?.getNotifications({ tag: this.activeTag }) ?? [];
      notifications.forEach(notification => notification.close());
    } catch { /* No active worker on this platform. */ }
  }

  private async poll(): Promise<void> {
    if (!this.sb.userId || !('Notification' in window) || Notification.permission !== 'granted') {
      if (this.notifiedTimerId !== null) await this.dismiss();
      this.notifiedTimerId = null;
      return;
    }
    try {
      const prefs = await this.client.from('notification_preferences')
        .select('enabled').eq('owner_id', this.sb.userId).maybeSingle();
      if (prefs.error || !prefs.data?.enabled) {
        if (this.notifiedTimerId !== null) await this.dismiss();
        this.notifiedTimerId = null; return;
      }
      const { data, error } = await this.client.from('time_entries')
        .select('id,started_at,activity_id,task_id')
        .is('ended_at', null).order('started_at', { ascending: true }).limit(1);
      if (error) return;
      const active = data?.[0];
      if (!active) {
        await this.dismiss();
        this.notifiedTimerId = null; return;
      }
      if (this.notifiedTimerId === Number(active.id)) return;
      const copy = {
        fr: { title: 'LifeOps — chronomètre actif', task: 'Un chronomètre de tâche est toujours actif.', activity: 'Un chronomètre d’activité est toujours actif.' },
        en: { title: 'LifeOps — active timer', task: 'A task timer is still running.', activity: 'An activity timer is still running.' },
        es: { title: 'LifeOps — temporizador activo', task: 'El temporizador de una tarea sigue activo.', activity: 'El temporizador de una actividad sigue activo.' }
      }[this.i18n.locale()];
      const title = copy.title;
      const body = active.task_id !== null ? copy.task : copy.activity;
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) await registration.showNotification(title, {
          body, tag: this.activeTag, icon: '/icons/icon-192x192.png'
        });
        else new Notification(title, { body, tag: this.activeTag });
      } else {
        new Notification(title, { body, tag: this.activeTag });
      }
      this.notifiedTimerId = Number(active.id);
    } catch { /* Polling failures must never affect application startup. */ }
  }
}
